import type {
  BodyRegion,
  ClipFraming,
  FootageAction,
  StockClipForReview,
} from '@borradh-workspace/api-client/types';
import {
  bodyRegionLabels,
  bodyRegionValues,
  clipFramingLabels,
  clipFramingValues,
  footageActionLabels,
  footageActionValues,
} from '@borradh-workspace/api-client/types';
import { Check, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

export interface Technique {
  slug: string;
  displayName: string;
  aliases?: string[];
  /** Its product types; a treatment with more than one lets a clip name which. */
  agents?: Array<{ slug: string; displayName: string }>;
}

/**
 * "Injection, Dermal filler", "Laser / IPL hair removal", or "Generic clip"
 * for a clip showing no treatment.
 */
export function clipTitle(
  clip: { techniqueSlug: string | null; agentSlug: string | null },
  techniques: Technique[]
): string {
  if (!clip.techniqueSlug) return 'Generic clip';
  const tech = techniques.find((t) => t.slug === clip.techniqueSlug);
  const name = tech?.displayName ?? clip.techniqueSlug;
  const product = clip.agentSlug
    ? tech?.agents?.find((a) => a.slug === clip.agentSlug)?.displayName
    : undefined;
  return product ? `${name}, ${product}` : name;
}

/** What the reviewer is deciding for one clip. */
export interface StockClipTags {
  treatmentScopes: import('@borradh-workspace/labels').StockTreatmentScope[];
  techniqueSlug: string | null;
  /** The product type shown, only for a treatment with several. */
  agentSlug: string | null;
  /**
   * No product named, and the clip fits every product of the treatment. Only
   * then can it serve a service whose product we know (or don't).
   */
  procedureNeutral: boolean;
  regions: BodyRegion[];
  footageActions: FootageAction[];
  isGeneric: boolean;
  framing: ClipFraming | null;
  visualDescription: string;
}

export const tagsFromClip = (clip: StockClipForReview): StockClipTags => ({
  techniqueSlug: clip.techniqueSlug,
  treatmentScopes: clip.treatmentScopes ?? [],
  agentSlug: clip.agentSlug,
  procedureNeutral: clip.procedureNeutral,
  regions: [...clip.regions],
  footageActions: clip.footageActions ?? [],
  isGeneric: clip.isGeneric,
  framing: clip.framing,
  visualDescription: clip.visualDescription ?? '',
});

export const isBodyRegionValue = (value: string): value is BodyRegion =>
  (bodyRegionValues as readonly string[]).includes(value);

const FACE: BodyRegion[] = [
  'full face',
  'forehead',
  'brow',
  'eyes',
  'nose',
  'cheeks',
  'lips',
  'chin',
  'jaw',
];
const BODY: BodyRegion[] = [
  'body-general',
  'neck',
  'chest',
  'abdomen',
  'flank',
  'back',
  'buttocks',
  'arms',
  'underarm',
  'hands',
  'legs',
  'thighs',
  'feet',
];
const GROUPED = new Set<string>([...FACE, ...BODY]);
const REGION_GROUPS: Array<{ label: string; regions: BodyRegion[] }> = [
  { label: 'Face', regions: FACE },
  { label: 'Body', regions: BODY },
  {
    label: 'Other',
    regions: bodyRegionValues.filter((r) => !GROUPED.has(r)),
  },
];

/** A chosen chip reads as chosen at a glance, in light and dark. */
const SELECTED_CHIP =
  'data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground';

interface StockClipTagsEditorProps {
  tags: StockClipTags;
  onChange: (tags: StockClipTags) => void;
  techniques: Technique[];
  disabled?: boolean;
  idPrefix: string;
}

/**
 * The tags a reviewer confirms: the treatment (searchable), the body areas
 * (chips, grouped), and the rarely needed details folded away. Everything is
 * chosen from the fixed lists, so nothing off-list can be entered.
 */
export function StockClipTagsEditor({
  tags,
  onChange,
  techniques,
  disabled,
  idPrefix,
}: StockClipTagsEditorProps) {
  const set = (patch: Partial<StockClipTags>) =>
    onChange({ ...tags, ...patch });
  const products =
    techniques.find((t) => t.slug === tags.techniqueSlug)?.agents ?? [];

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-treatment`}>Treatment</Label>
        <TreatmentPicker
          id={`${idPrefix}-treatment`}
          value={tags.techniqueSlug}
          techniques={techniques}
          disabled={disabled}
          onChange={(techniqueSlug) => {
            const allowed = techniques.find(
              (t) => t.slug === techniqueSlug
            )?.agents;
            set({
              techniqueSlug,
              agentSlug: allowed?.some((a) => a.slug === tags.agentSlug)
                ? tags.agentSlug
                : null,
              // Neutral was a statement about the old treatment's products.
              procedureNeutral:
                techniqueSlug === tags.techniqueSlug
                  ? tags.procedureNeutral
                  : false,
            });
          }}
        />
      </div>

      {products.length > 1 ? (
        <div className="space-y-2">
          <span className="font-medium text-sm">Product</span>
          <p className="text-muted-foreground text-xs">
            Only when you can see which. Leave it empty otherwise.
          </p>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={1}
            disabled={disabled}
            className="w-full flex-wrap"
            aria-label="Product"
            value={tags.agentSlug ?? ''}
            onValueChange={(value) =>
              set({
                agentSlug: value || null,
                procedureNeutral: value ? false : tags.procedureNeutral,
              })
            }
          >
            {products.map((product) => (
              <ToggleGroupItem
                key={product.slug}
                value={product.slug}
                className={cn('rounded-full', SELECTED_CHIP)}
              >
                {product.displayName}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {tags.agentSlug ? null : (
            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id={`${idPrefix}-neutral`}
                checked={tags.procedureNeutral}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  set({ procedureNeutral: checked === true })
                }
              />
              <div className="space-y-0.5">
                <Label
                  htmlFor={`${idPrefix}-neutral`}
                  className="font-normal text-sm"
                >
                  Fits any of these products
                </Label>
                <p className="text-muted-foreground text-xs">
                  Tick only when nothing in the clip shows which one, such as
                  skin being cleansed before the treatment. Unticked, a clip
                  with no product is not used.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <FootageActionChips
        value={tags.footageActions}
        disabled={disabled}
        onChange={(footageActions) => set({ footageActions })}
      />
      <BodyAreaChips
        value={tags.regions}
        disabled={disabled}
        onChange={(regions) => set({ regions })}
      />

      <MoreDetails
        tags={tags}
        set={set}
        disabled={disabled}
        idPrefix={idPrefix}
      />
    </div>
  );
}

/** Body areas as chips, grouped Face / Body / Other. */
export function BodyAreaChips({
  value,
  disabled,
  onChange,
}: {
  value: BodyRegion[];
  disabled?: boolean;
  onChange: (regions: BodyRegion[]) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium text-sm">Body areas</span>
        {value.length > 0 ? (
          <button
            type="button"
            className="text-muted-foreground text-xs underline-offset-2 hover:underline"
            onClick={() => onChange([])}
            disabled={disabled}
          >
            Clear
          </button>
        ) : (
          <span className="text-muted-foreground text-xs">None selected</span>
        )}
      </div>
      {REGION_GROUPS.map((group) => (
        <div
          key={group.label}
          className="grid gap-2 sm:grid-cols-[72px_1fr] sm:items-start"
        >
          <span className="pt-1.5 text-muted-foreground text-xs">
            {group.label}
          </span>
          <ToggleGroup
            type="multiple"
            variant="outline"
            size="sm"
            spacing={1}
            disabled={disabled}
            className="w-full flex-wrap"
            aria-label={`${group.label} areas`}
            value={value.filter((r) => group.regions.includes(r))}
            onValueChange={(selected) =>
              onChange([
                ...value.filter((r) => !group.regions.includes(r)),
                ...selected.filter(isBodyRegionValue),
              ])
            }
          >
            {group.regions.map((region) => (
              <ToggleGroupItem
                key={region}
                value={region}
                className={cn('rounded-full', SELECTED_CHIP)}
              >
                {bodyRegionLabels[region]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      ))}
    </div>
  );
}

/** A searchable treatment list; `null` is the "no treatment" choice. */
export function TreatmentPicker({
  id,
  value,
  techniques,
  disabled,
  noneLabel = 'No treatment shown',
  className,
  onChange,
}: {
  id: string;
  className?: string;
  value: string | null;
  techniques: Technique[];
  disabled?: boolean;
  /** What choosing no treatment means here. */
  noneLabel?: string;
  onChange: (slug: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = techniques.find((t) => t.slug === value);
  const choose = (slug: string | null) => {
    onChange(slug);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span
            className={cn('truncate', !selected && 'text-muted-foreground')}
          >
            {selected ? selected.displayName : noneLabel}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command
          filter={(value, search) =>
            search
              .trim()
              .toLowerCase()
              .split(/\s+/)
              .every((term) => value.toLowerCase().includes(term))
              ? 1
              : 0
          }
        >
          <CommandInput placeholder="Search treatments…" />
          <CommandList>
            <CommandEmpty>No treatment matches.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="none no treatment shown generic"
                onSelect={() => choose(null)}
              >
                <Check
                  className={cn(
                    'mr-2 size-4',
                    value === null ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {noneLabel}
              </CommandItem>
              {techniques.map((t) => (
                <CommandItem
                  key={t.slug}
                  value={`${t.displayName} ${t.slug} ${(t.aliases ?? []).join(' ')}`}
                  onSelect={() => choose(t.slug)}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value === t.slug ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {t.displayName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function MoreDetails({
  tags,
  set,
  disabled,
  idPrefix,
}: {
  tags: StockClipTags;
  set: (patch: Partial<StockClipTags>) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  const [open, setOpen] = useState(false);
  const summary = [
    tags.framing ? clipFramingLabels[tags.framing] : 'Framing not set',
    tags.isGeneric ? 'Generic' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm"
        >
          <span className="font-medium">More details</span>
          <span className="flex items-center gap-2 text-muted-foreground text-xs">
            {summary}
            <ChevronDown
              className={cn(
                'size-4 transition-transform',
                open && 'rotate-180'
              )}
            />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 border-t px-3 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor={`${idPrefix}-generic`}>Generic clip</Label>
            <p className="text-muted-foreground text-xs">
              Shows no specific treatment (a room, a product, a consultation).
            </p>
          </div>
          <Switch
            id={`${idPrefix}-generic`}
            checked={tags.isGeneric}
            disabled={disabled}
            onCheckedChange={(isGeneric) => set({ isGeneric })}
          />
        </div>

        <div className="space-y-2">
          <span className="font-medium text-sm">Framing</span>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            disabled={disabled}
            aria-label="Framing"
            value={tags.framing ?? ''}
            onValueChange={(value) =>
              set({
                framing: (clipFramingValues as readonly string[]).includes(
                  value
                )
                  ? (value as ClipFraming)
                  : null,
              })
            }
          >
            {clipFramingValues.map((f) => (
              <ToggleGroupItem key={f} value={f} className={SELECTED_CHIP}>
                {clipFramingLabels[f]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-description`}>
            Description (what is visible, no brands)
          </Label>
          <Textarea
            id={`${idPrefix}-description`}
            value={tags.visualDescription}
            disabled={disabled}
            rows={3}
            onChange={(e) => set({ visualDescription: e.target.value })}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Shared closed choices; service choices mean any selected action is acceptable. */
export function FootageActionChips({
  value,
  onChange,
  disabled,
}: {
  value: FootageAction[];
  onChange: (actions: FootageAction[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <span className="font-medium text-sm">Actions / procedures</span>
      <ToggleGroup
        type="multiple"
        variant="outline"
        size="sm"
        spacing={1}
        disabled={disabled}
        className="w-full flex-wrap"
        aria-label="Actions / procedures"
        value={value}
        onValueChange={(selected) =>
          onChange(
            selected.filter((s): s is FootageAction =>
              Object.hasOwn(footageActionLabels, s)
            )
          )
        }
      >
        {footageActionValues.map((action) => (
          <ToggleGroupItem
            key={action}
            value={action}
            className={cn('rounded-full', SELECTED_CHIP)}
          >
            {footageActionLabels[action]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
